import { AddOutlined, PolicyOutlined } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Permission } from '@procureai/permissions';
import { Restricted } from '@procureai/permissions-ui';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace, KnowledgeRepositoryDomainType } from '~/common/types';
import { DataGridQueryBuilder, useDataGridQueryBuilderQuery } from '~/components/DataGridQueryBuilder';
import { DataGridWidget } from '~/components/DataGridWidget';
import { Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { PageLayout } from '~/components/PageLayout/PageLayout';

import { ProcurementKnowledgeAddDialog } from './components/ProcumentKnowledgeDialogs/ProcurementKnowledgeAddDialog';
import { ProcurementKnowledgeTitle } from './components/ProcurementKnowledgeTitle';
import { useProcurementKnowledgeDataGridConfig } from './ProcurementKnowledgeList.datagrid';
import { useProcurementKnowledgeListDataGridQueryBuilderConfig } from './ProcurementKnowledgeList.queryBuilder';
import { ProcurementKnowledgeDataGridColumn } from './ProcurementKnowledgeList.types';
import { PROCUREMENT_KNOWLEDGE_LIST_QUERY } from './ProcurementKnowledgeListQuery.gql';

const domain = KnowledgeRepositoryDomainType.PROCUREMENT_KNOWLEDGE;

export const ProcurementKnowledgeList = () => {
  const { t } = useTranslation(I18nNamespace.knowledgeRepository);
  const apiRef = useGridApiRef();
  const { queryId, query, onQueryChange, onQueryIdChange, rulesTotal, loading } = useDataGridQueryBuilderQuery({
    domain,
  });
  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId });

  // flags for adding or editing docs (by id)
  const [isAddingDoc, setIsAddingDoc] = useState(false);

  return (
    <PageLayout
      fullHeight
      renderHeader={
        <>
          <PageHeaderStatic
            title={<ProcurementKnowledgeTitle />}
            icon={<WidgetIcon color="primary" icon={<PolicyOutlined />} />}
            maxWidth={false}
            right={
              <Stack spacing={3} direction="row">
                <Filters
                  {...filtersProps}
                  selected={selected}
                  onFilterSelect={(filter) => {
                    onQueryIdChange(filter.queryId);
                  }}
                />
                <Restricted
                  needs={{
                    permission: Permission.KnowledgeEdit,
                  }}
                >
                  <Button
                    onClick={() => setIsAddingDoc(true)}
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={<AddOutlined />}
                  >
                    {t('ProcurementKnowledge.button.add')}
                  </Button>
                </Restricted>
              </Stack>
            }
          />
          <DataGridQueryBuilder
            query={query}
            clearDisabled={rulesTotal === 0}
            saveDisabled={saveStatus === FilterSaveStatus.DISABLED}
            onQueryChange={onQueryChange}
            onSave={onFilterSave}
            selectedFilterName={selected?.name}
            useConfig={useProcurementKnowledgeListDataGridQueryBuilderConfig}
          />
        </>
      }
    >
      {/* data grid to display current documents */}
      <DataGridWidget
        apiRef={apiRef}
        domain={domain}
        loading={loading}
        filter={query}
        pinnedColumns={{
          right: [ProcurementKnowledgeDataGridColumn.ACTIONS],
        }}
        filterCount={rulesTotal}
        useConfig={useProcurementKnowledgeDataGridConfig}
        initialNavigateDetail={() => null}
        loadingProps={{
          query: PROCUREMENT_KNOWLEDGE_LIST_QUERY,
          getNodes: (data) => data.sevenbridges.documents.nodes,
          getTotalCount: (data) => data.sevenbridges.documents.totalCount || 0,
        }}
      />
      {/* dialog to add a new document */}
      {isAddingDoc && <ProcurementKnowledgeAddDialog open={isAddingDoc} onClose={() => setIsAddingDoc(false)} />}
    </PageLayout>
  );
};
